import { useMemo, useState } from 'react';
import {
  Box,
  DataTable,
  DataTableHeader,
  DataTableHeaderRow,
  DataTableHeaderSavedViews,
  DataTableTable,
  Panel,
  PanelInset,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { getUsageCount } from '../utils/usage';
import { offeredUsageSurfaces, type UsageScope } from '../utils/usage-surfaces';
import type { DataIndexUsage, DataIndexUsageKey } from '../utils/types';
import { CampaignUsage, USAGE_ICONS, UsageEmptyState, usageColumnsFor } from './usage-columns';

/**
 * Where an item is used, one surface at a time.
 *
 * The saved views are the surfaces, each counted, so the size of the problem
 * is visible before anything is opened. The rows themselves are per-surface
 * and land with their own tables.
 */

/** What the workspace calls the surfaces that are named after a product noun. */
export type UsageViewTerms = {
  campaignsTitlePlural: string;
  newslettersTitlePlural: string;
};

export type UsageView = {
  id: DataIndexUsageKey;
  /** The surface's name on its own, for the caption and the empty state. */
  label: string;
  /** The name the view tab shows, which carries the count. */
  name: string;
};

function countedName(usage: DataIndexUsage, id: DataIndexUsageKey, label: string): string {
  return `${label} (${String(getUsageCount(usage, id))})`;
}

/**
 * The surface shown before anything is chosen. A Builder plan has no
 * campaigns, so its default is the next thing it does have.
 */
export function defaultUsageView(usage: DataIndexUsage, scope: UsageScope, terms: UsageViewTerms): UsageView {
  return scope.isBuilder
    ? {
        id: 'newsletters',
        label: terms.newslettersTitlePlural,
        name: countedName(usage, 'newsletters', terms.newslettersTitlePlural),
      }
    : {
        id: 'campaigns',
        label: terms.campaignsTitlePlural,
        name: countedName(usage, 'campaigns', terms.campaignsTitlePlural),
      };
}

/**
 * The order the views are shown in, which is not the drawer's accordion
 * order: the page this replaces lists newsletters second, beside segments.
 */
const VIEW_ORDER: DataIndexUsageKey[] = [
  'segments',
  'campaigns',
  'newsletters',
  'templates',
  'design_studio',
  'snippets',
  'layouts',
];

/**
 * The surfaces offered beside the default one, counted, in the order they
 * are shown. The default is not among them: it is what the table falls back
 * to when no view is chosen.
 */
export function usageViews(usage: DataIndexUsage, scope: UsageScope, terms: UsageViewTerms): UsageView[] {
  const labels = {
    segments: 'Segments',
    campaigns: terms.campaignsTitlePlural,
    newsletters: terms.newslettersTitlePlural,
    templates: 'Templates',
    design_studio: 'Design Studio',
    snippets: 'Snippets',
    layouts: 'Layouts',
  } satisfies Record<DataIndexUsageKey, string>;
  const defaultId = defaultUsageView(usage, scope, terms).id;

  return offeredUsageSurfaces(VIEW_ORDER, scope)
    .filter((id) => id !== defaultId)
    .map((id) => ({ id, label: labels[id], name: countedName(usage, id, labels[id]) }));
}

/**
 * The height the rows are held in, as the page this replaces holds them:
 * twice `space-3200`, fixed rather than capped
 * (`data-index-item/scroll-container/index.css.ts`).
 */
const SCROLL_HEIGHT = `calc(${themeVars.space['3200']} * 2)`;

type UsageTabProps = {
  workspaceId: number;
  /** The item's name, which the empty states say is not used anywhere. */
  itemName: string;
  usage: DataIndexUsage;
  scope: UsageScope;
  terms: UsageViewTerms;
};

export function UsageTab({ workspaceId, itemName, usage, scope, terms }: UsageTabProps) {
  // Which surface is being read is a glance, not a place: it is not worth a
  // URL, and Ember does not give it one either.
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const views = usageViews(usage, scope, terms);
  const defaultView = defaultUsageView(usage, scope, terms);
  const active = views.find((view) => view.id === activeViewId) ?? defaultView;
  const label = active.label;
  // Both rebuild the table, and the rows are the same rows until the view
  // changes.
  const rows = useMemo(() => [...(usage[active.id] ?? [])], [usage, active.id]);
  const columns = useMemo(() => usageColumnsFor(active.id, workspaceId), [active.id, workspaceId]);
  // Named rather than found: left to look for itself, the table settles on
  // its own horizontal-scroll wrapper, which has no height of its own, and
  // renders every row. The page this replaces hands its box over the same
  // way (`data-index-item/scroll-container/index.gts`).
  const [scrollBox, setScrollBox] = useState<HTMLElement | null>(null);
  const virtualizerOptions = useMemo(() => ({ getScrollElement: () => scrollBox }), [scrollBox]);

  return (
    // An attribute can be used by hundreds of campaigns. The page this
    // replaces never pages them: it holds the whole table in a fixed-height
    // box that scrolls, and virtualizes the rows inside it
    // (`data-index-item/usage-table/index.gts`, which passes no
    // `withPagination`).
    <Box ref={setScrollBox} overflowY="auto" overflowX="hidden" style={{ height: SCROLL_HEIGHT }}>
      <DataTable
        caption={`${label} usage`}
        data={rows}
        columns={columns}
        withVirtualizer
        virtualizerOptions={virtualizerOptions}
        withSavedViews
        savedViews={{
          views,
          activeViewId,
          onActiveViewChange: setActiveViewId,
          defaultViewName: defaultView.name,
        }}
        // Only a campaign has more to say than its own row: which capacities
        // it uses the item in.
        withRowExpanding={active.id === 'campaigns'}
        getRowCanExpand={() => active.id === 'campaigns'}
        expandedRowComponent={CampaignUsage}
        emptyStateComponent={() => (
          <UsageEmptyState surface={active.id} label={label} itemName={itemName} icon={USAGE_ICONS[active.id]} />
        )}
      >
        {/* Composed like the list's own table (`components/data-index-table.tsx`):
            the header above the rows, and the rows inset. */}
        <Panel>
          <DataTableHeader>
            {(header) =>
              header.shouldShowSavedViews === true ? (
                <DataTableHeaderRow>
                  <DataTableHeaderSavedViews />
                </DataTableHeaderRow>
              ) : null
            }
          </DataTableHeader>
          <PanelInset>
            <DataTableTable />
          </PanelInset>
        </Panel>
      </DataTable>
    </Box>
  );
}
